import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/AccountsAPI/verifyTLAgencyAPI';

const err = (e, fallback) => e?.ErrorDetails?.ErrorMessage || e?.Message || e?.message || fallback;

export const fetchVerifyTLAgencyQueue = createAsyncThunk(
    'verifyTLAgency/fetchQueue',
    async (roleId, { rejectWithValue }) => {
        try { return await api.getVerificationTLAgencies(roleId); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch term loan agency verification queue')); }
    }
);

export const fetchVerifyTLAgencyDetail = createAsyncThunk(
    'verifyTLAgency/fetchDetail',
    async (agencyCode, { rejectWithValue }) => {
        try { return await api.getTLAgencyByCode(agencyCode); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch agency details')); }
    }
);

export const submitApproveTLAgency = createAsyncThunk(
    'verifyTLAgency/approve',
    async (payload, { rejectWithValue }) => {
        try { return await api.approveTLAgency(payload); }
        catch (e) { return rejectWithValue(err(e, 'Failed to submit agency verification')); }
    }
);

export const submitUpdateTLAgency = createAsyncThunk(
    'verifyTLAgency/update',
    async (payload, { rejectWithValue }) => {
        try { return await api.updateReturnedTLAgency(payload); }
        catch (e) { return rejectWithValue(err(e, 'Failed to update returned agency')); }
    }
);

const initialState = {
    queue: [], queueLoading: false, queueError: null,
    detail: null, detailLoading: false, detailError: null,
    verifyLoading: false, updateLoading: false,
};

const verifyTLAgencySlice = createSlice({
    name: 'verifyTLAgency',
    initialState,
    reducers: {
        clearVerifyTLAgencyDetail: (s) => { s.detail = null; s.detailError = null; s.detailLoading = false; },
        resetVerifyTLAgency: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchVerifyTLAgencyQueue.pending, (s) => { s.queueLoading = true; s.queueError = null; })
            .addCase(fetchVerifyTLAgencyQueue.fulfilled, (s, a) => { s.queueLoading = false; s.queue = a.payload?.Data || []; })
            .addCase(fetchVerifyTLAgencyQueue.rejected, (s, a) => { s.queueLoading = false; s.queueError = a.payload; s.queue = []; });

        builder
            .addCase(fetchVerifyTLAgencyDetail.pending, (s) => { s.detailLoading = true; s.detailError = null; s.detail = null; })
            .addCase(fetchVerifyTLAgencyDetail.fulfilled, (s, a) => { s.detailLoading = false; s.detail = a.payload?.Data || null; })
            .addCase(fetchVerifyTLAgencyDetail.rejected, (s, a) => { s.detailLoading = false; s.detailError = a.payload; s.detail = null; });

        builder
            .addCase(submitApproveTLAgency.pending, (s) => { s.verifyLoading = true; })
            .addCase(submitApproveTLAgency.fulfilled, (s) => { s.verifyLoading = false; })
            .addCase(submitApproveTLAgency.rejected, (s) => { s.verifyLoading = false; });

        builder
            .addCase(submitUpdateTLAgency.pending, (s) => { s.updateLoading = true; })
            .addCase(submitUpdateTLAgency.fulfilled, (s) => { s.updateLoading = false; })
            .addCase(submitUpdateTLAgency.rejected, (s) => { s.updateLoading = false; });
    },
});

export const { clearVerifyTLAgencyDetail, resetVerifyTLAgency } = verifyTLAgencySlice.actions;

export const selectVTAQueue = (s) => s.verifyTLAgency.queue;
export const selectVTAQueueLoading = (s) => s.verifyTLAgency.queueLoading;
export const selectVTAQueueError = (s) => s.verifyTLAgency.queueError;
export const selectVTADetail = (s) => s.verifyTLAgency.detail;
export const selectVTADetailLoading = (s) => s.verifyTLAgency.detailLoading;
export const selectVTADetailError = (s) => s.verifyTLAgency.detailError;
export const selectVTAVerifyLoading = (s) => s.verifyTLAgency.verifyLoading;
export const selectVTAUpdateLoading = (s) => s.verifyTLAgency.updateLoading;

export default verifyTLAgencySlice.reducer;
